// TFVF Module Detail — renders a single module's readable content chunks,
// linked assets, quiz (if any), and progress state.
import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Loader2, ArrowLeft, Download, FileText, CheckCircle2, XCircle,
  BookOpen, Clock, ShieldCheck,
} from 'lucide-react';
import { API, authHeaders, renderMarkdown } from './tfvfShared';
import { useAuth } from '../../../contexts/AuthContext';

const TfvfModule = () => {
  const { itemId: moduleId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [m, setM] = useState(null);
  const [loading, setLoading] = useState(true);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!token || !moduleId) return;
    setLoading(true);
    setResult(null);
    setAnswers({});
    axios.get(`${API}/tfvf/modules/${moduleId}`, { headers: authHeaders(token) })
      .then(r => setM(r.data))
      .catch(() => toast.error('Module not found'))
      .finally(() => setLoading(false));
  }, [moduleId, token]);

  const submit = async () => {
    if (!m) return;
    const total = (m.quiz || []).length;
    if (Object.keys(answers).length !== total) {
      toast.info('Answer every question first');
      return;
    }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/tfvf/modules/${moduleId}/quiz`, { answers }, { headers: authHeaders(token) });
      setResult(r.data);
      if (r.data.passed) toast.success(`Passed — ${r.data.score_pct}%`);
      else toast.warning(`Not passed — ${r.data.score_pct}% (need ${r.data.pass_score}%). Try again.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Submit failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-[color:var(--tf-forest)]/50" /></div>;
  if (!m) return <p>Module not found. <button onClick={() => navigate('/training')} className="text-[color:var(--tf-gold-2)] underline">Back to My Training</button></p>;

  const progress = m.progress || {};
  const done = !!progress.completed_at;

  return (
    <div className="space-y-5" data-testid="tfvf-module-page">
      {/* Back + header */}
      <button onClick={() => navigate('/training')} className="text-xs text-[color:var(--tf-muted)] hover:text-[color:var(--tf-forest)] inline-flex items-center gap-1">
        <ArrowLeft className="w-3 h-3" /> All modules
      </button>

      <header className="tf-card rounded-xl p-5">
        <div className="flex items-start gap-3 flex-wrap">
          <div className="flex-1">
            <p className="tf-eyebrow">{m.module_id} · {m.audience}</p>
            <h1 className="text-2xl font-bold text-[color:var(--tf-forest)] mt-0.5">{m.title}</h1>
            <p className="text-sm text-[color:var(--tf-slate)] mt-1">{m.summary}</p>
            <div className="mt-3 flex items-center gap-3 text-xs text-[color:var(--tf-muted)] flex-wrap">
              <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" />{m.duration_minutes} minutes</span>
              <span>· v{m.version}</span>
              {m.required && <span className="tf-badge-gold text-[10px] font-bold px-2 py-0.5 rounded-full">Required</span>}
              {done && (
                <span className="tf-badge-forest text-[10px] font-bold px-2 py-0.5 rounded-full inline-flex items-center gap-1">
                  <CheckCircle2 className="w-3 h-3" /> Completed {new Date(progress.completed_at).toLocaleDateString()}
                </span>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Content chunks */}
      {(m.content_chunks || []).length > 0 && (
        <section className="tf-card rounded-xl p-6" data-testid="tfvf-module-content">
          <div className="space-y-4">
            {m.content_chunks.map((chunk, i) => (
              <div key={i}>
                <h2 className="text-lg font-bold text-[color:var(--tf-forest)] flex items-center gap-2 pb-1 border-b border-[color:var(--tf-forest)]/10">
                  <BookOpen className="w-4 h-4" style={{ color: 'var(--tf-gold)' }} />
                  {chunk.title}
                </h2>
                <p className="text-[10px] text-[color:var(--tf-muted)] italic mb-2">Source: {chunk.source_doc}</p>
                <div className="tf-prose text-sm">{renderMarkdown(chunk.body_markdown)}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Assets */}
      {(m.assets || []).length > 0 && (
        <section className="tf-card rounded-xl p-5" data-testid="tfvf-module-assets">
          <h3 className="tf-eyebrow mb-2">Downloadable resources</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {m.assets.map(a => (
              <a
                key={a.id}
                href={`${API}${a.download_path}`}
                onClick={(e) => {
                  e.preventDefault();
                  // Use fetch+blob so we can pass the auth header.
                  fetch(`${API}${a.download_path}`, { headers: authHeaders(token) })
                    .then(r => r.blob())
                    .then(b => {
                      const u = URL.createObjectURL(b);
                      const link = document.createElement('a');
                      link.href = u; link.download = a.filename; link.click();
                      URL.revokeObjectURL(u);
                    });
                }}
                className="tf-badge-forest rounded-lg px-3 py-2 text-sm inline-flex items-center gap-2 hover:opacity-80"
                data-testid={`tfvf-asset-${a.id}`}
              >
                <FileText className="w-4 h-4" />
                <span className="flex-1 truncate">{a.title}</span>
                <Download className="w-3 h-3" />
              </a>
            ))}
          </div>
        </section>
      )}

      {/* Quiz */}
      {(m.quiz || []).length > 0 && (
        <section className="tf-card rounded-xl p-6" data-testid="tfvf-module-quiz">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-bold text-[color:var(--tf-forest)] flex items-center gap-2">
              <ShieldCheck className="w-5 h-5" style={{ color: 'var(--tf-gold)' }} />
              Knowledge Check
            </h2>
            <p className="text-xs text-[color:var(--tf-muted)]">Pass mark {m.pass_score || 85}% · retake any time</p>
          </div>
          <div className="space-y-4">
            {m.quiz.map((q, qi) => (
              <div key={q.id} className="border-b border-[color:var(--tf-forest)]/10 pb-3 last:border-0" data-testid={`quiz-q-${q.id}`}>
                <p className="text-sm font-semibold text-[color:var(--tf-forest)] mb-2">{qi + 1}. {q.question}</p>
                <div className="space-y-1.5">
                  {(q.options || []).map((opt, oi) => (
                    <label
                      key={oi}
                      className={`flex items-center gap-2 p-2 rounded cursor-pointer text-sm border ${
                        answers[q.id] === opt ? 'border-[color:var(--tf-gold)] bg-[color:var(--tf-parchment)]' : 'border-[color:var(--tf-forest)]/10 hover:bg-[color:var(--tf-parchment)]/50'
                      }`}
                    >
                      <input
                        type="radio"
                        name={q.id}
                        checked={answers[q.id] === opt}
                        onChange={() => setAnswers(a => ({ ...a, [q.id]: opt }))}
                        data-testid={`quiz-${q.id}-opt-${oi}`}
                      />
                      <span>{opt}</span>
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <button
            onClick={submit}
            disabled={submitting || Object.keys(answers).length !== m.quiz.length}
            className="tf-btn-primary mt-4 px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-50"
            data-testid="tfvf-submit-quiz"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin inline mr-1" /> : null}
            Submit answers
          </button>

          {result && (
            <div className={`mt-4 p-3 rounded-lg border ${result.passed ? 'bg-emerald-50 border-emerald-300' : 'bg-amber-50 border-amber-300'}`} data-testid="tfvf-quiz-result">
              <p className="text-sm font-semibold">
                {result.passed ? <span className="text-emerald-700 inline-flex items-center gap-1"><CheckCircle2 className="w-4 h-4" /> Passed — {result.score_pct}%</span>
                  : <span className="text-amber-700 inline-flex items-center gap-1"><XCircle className="w-4 h-4" /> Not yet — {result.score_pct}% (need {result.pass_score}%)</span>}
              </p>
              <div className="mt-2 space-y-1">
                {result.per_question.map(pq => (
                  <p key={pq.id} className="text-xs">
                    <span className={pq.correct ? 'text-emerald-700' : 'text-rose-700'}>{pq.correct ? '✓' : '✗'}</span>
                    {' '}<span className="text-[color:var(--tf-slate)]">{pq.question}</span>
                    {!pq.correct && <span className="text-[color:var(--tf-muted)]"> — correct: {pq.correct_answer}</span>}
                  </p>
                ))}
              </div>
            </div>
          )}
        </section>
      )}

      {/* Acknowledgement-only modules */}
      {m.assessment_type === 'acknowledgement' && (
        <section className="tf-card rounded-xl p-6" data-testid="tfvf-module-ack-cta">
          <p className="text-sm text-[color:var(--tf-slate)]">
            This module completes when you sign the platform acknowledgements.
          </p>
          <Link to="/training/acknowledgements" className="tf-btn-gold inline-block mt-3 px-4 py-2 rounded-lg text-sm font-semibold">
            Sign acknowledgements →
          </Link>
        </section>
      )}
    </div>
  );
};

export default TfvfModule;
